import {useStore} from '@/store';
import {Commit} from 'vuex';
import {APP_MODULE} from '@/store/state';
import {RecommendationsContextWithOffers} from '@/views/amex/app-types';
import {loadSourcePOI} from '@/store/api/load-source-poi';
import { fetchAdditionalPois } from '@/store/api/fetch-additional-pois';
import debounce from 'awesome-debounce-promise';
import { COMMON_DEBOUNCE_TIME_MS } from '@/config/common-constants';

const MODULE_NAME = 'ADDITIONAL_POIS';

type AdditionalPoisStore = {
  pois: RecommendationsContextWithOffers[];
  poisMap: Map<string, RecommendationsContextWithOffers>;
};

const Getters = {
  GET: `${MODULE_NAME}/GET`,
  GET_MAP: `${MODULE_NAME}/GET_MAP`,
};

const Actions = {
  ADD_ITEMS: `${MODULE_NAME}/ADD_ITEMS`,
  CLEAR: `${MODULE_NAME}/CLEAR`,
  FETCH: `${MODULE_NAME}/FETCH`,
};

const Mutations = {
  SET: `${MODULE_NAME}/SET`,
  APPEND_ITEMS: `${MODULE_NAME}/APPEND_ITEMS`,
};

type ApiPayload = {
  pois: RecommendationsContextWithOffers[];
};

const store = useStore();

export const AdditionalPoisModule = {
  fetchMissingPois: (ids: string[]) => {
    const missingIds = ids.filter((id) => !AdditionalPoisModule.getPoiById(id));
    if (missingIds.length) {
      AdditionalPoisModule.fetchPoisByIds(missingIds);
    }
  },
  fetchPoisByIds: debounce(async (ids: string[] = []) => {
    const pois = await fetchAdditionalPois(ids);
    if (pois && pois.length) {
      AdditionalPoisModule.addPois(pois);
    }
  }, COMMON_DEBOUNCE_TIME_MS),
  getPois: (): RecommendationsContextWithOffers[] => {
    return store.getters[`${APP_MODULE}/${Getters.GET}`];
  },
  getPoiById: (id: string): RecommendationsContextWithOffers | null => {
    const map = store.getters[`${APP_MODULE}/${Getters.GET_MAP}`];
    return map.get(id) || null;
  },

  addOnePoi: (poi: RecommendationsContextWithOffers) => {
    return store.dispatch(`${APP_MODULE}/${Actions.ADD_ITEMS}`, [poi]);
  },

  addPois: (pois: RecommendationsContextWithOffers[]) => {
    return store.dispatch(`${APP_MODULE}/${Actions.ADD_ITEMS}`, pois);
  },

  clearPois: () => {
    return store.dispatch(`${APP_MODULE}/${Actions.CLEAR}`);
  },

  fetchSourcePoi: (id: string) => {
    return store.dispatch(`${APP_MODULE}/${Actions.FETCH}`, id);
  },

  state: {
    pois: [],
    poisMap: new Map<string, RecommendationsContextWithOffers>(),
  },

  getters: {
    [Getters.GET]: (state: AdditionalPoisStore): RecommendationsContextWithOffers[] => {
      return state.pois || [];
    },
    [Getters.GET_MAP]: (state: AdditionalPoisStore): Map<string, RecommendationsContextWithOffers> => {
      return state.poisMap || new Map();
    },
  },

  actions: {
    [Actions.ADD_ITEMS]: ({commit}: {commit: Commit}, pois: RecommendationsContextWithOffers[]) => {
      commit(Mutations.SET, {pois: [...AdditionalPoisModule.getPois(), ...pois]});
    },
    [Actions.CLEAR]: ({commit}: {commit: Commit}) => {
      commit(Mutations.SET, {pois: []});
    },
    [Actions.FETCH]: async ({commit}: {commit: Commit}, id: string) => {
      try {
        const poi = await loadSourcePOI(id);
        if (poi) {
          commit(Mutations.SET, {pois: [poi]});
          return poi;
        }
        return [];
      } catch (e) {
        console.error('AdditionalPOIsModule: failed to fetch POI', e);
      }
    },
  },

  mutations: {
    [Mutations.SET]: (state: AdditionalPoisStore, {pois}: ApiPayload) => {
      state.poisMap = new Map<string, RecommendationsContextWithOffers>(pois.map((poi) => ([poi.id, poi])));
      state.pois = [...state.poisMap.values()];
    },
    [Mutations.APPEND_ITEMS]: (state: AdditionalPoisStore, {pois}: ApiPayload) => {
      state.poisMap = new Map<string, RecommendationsContextWithOffers>([...state.pois, ...pois].map((poi) => ([poi.id, poi])));
      state.pois = [...state.poisMap.values()];
    },
  },
};
